import { useTranslation } from 'react-i18next';

import { PaymentDeviceAccessory, PaymentDeviceRentalRequestStatus, PaymentDeviceStatus } from '@/models/common/PaymentDeviceEnums';
import { deviceStatusColor, requestStatusColor } from '@/helpers/paymentDevices';
import { accessoriesText } from '@/helpers/paymentDeviceAccessories';

const Dot = ({ color }: { color: string }) => (
  <span className="w-[10px] h-[10px] rounded-full shrink-0 inline-block" style={{ backgroundColor: color }} />
);

export const DeviceStatusChip = ({ status }: { status: PaymentDeviceStatus }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-2">
      <Dot color={deviceStatusColor(status)} />
      <span>{t(`paymentDevices.status.${status}`)}</span>
    </span>
  );
};

export const RequestStatusChip = ({ status }: { status: PaymentDeviceRentalRequestStatus }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-2">
      <Dot color={requestStatusColor(status)} />
      <span>{t(`paymentDevices.requestStatus.${status}`)}</span>
    </span>
  );
};

export const OverdueTag = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center rounded-full px-2 py-[1px] body-sm-semibold bg-red-50 text-red-600 border border-red-200">
      {t('paymentDevices.overdue')}
    </span>
  );
};

/** A device in one continuous rental for 3 months or more (HAT-1434) - the office's reminder. */
export const LongRentalTag = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center rounded-full px-2 py-[1px] body-sm-semibold bg-orange-50 text-orange-600 border border-orange-200">
      {t('paymentDevices.longRental.tag')}
    </span>
  );
};

/** Accessories that went out with the device and did not come back (HAT-1434). Nothing when none is missing. */
export const MissingAccessoriesTag = ({ items }: { items: PaymentDeviceAccessory[] | null | undefined }) => {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;
  return (
    <span className="inline-flex items-center rounded-full px-2 py-[1px] body-sm-semibold bg-red-50 text-red-600 border border-red-200">
      {t('paymentDevices.accessories.missingTag', { items: accessoriesText(items, t) })}
    </span>
  );
};
